import React, { useState } from 'react';
import { Check, X, DollarSign, Bot, Users, Star, Zap, Globe, Camera, Image, MessageCircle, Sparkles, Clock, ChevronRight, ChevronLeft, CreditCard, HelpCircle, CheckCircle, Percent, Settings, ArrowRight, Instagram, Twitter, ShoppingBag, Award, Lock, Crown, Info } from 'lucide-react';
interface PlanUpsellPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPlan: (plan: string) => void;
}
export const PlanUpsellPopup: React.FC<PlanUpsellPopupProps> = ({
  isOpen,
  onClose,
  onSelectPlan
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [selectedAddons, setSelectedAddons] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const toggleAddon = (addon: string) => {
    if (selectedAddons.includes(addon)) {
      setSelectedAddons(selectedAddons.filter(a => a !== addon));
    } else {
      setSelectedAddons([...selectedAddons, addon]);
    }
  };
  const handleSelectPlan = (plan: string) => {
    setSelectedPlan(plan);
    setCurrentStep(2); // Move to confirmation screen
  };
  const handleConfirm = () => {
    if (selectedPlan === 'basic') {
      // Skip checkout for free plan
      setCurrentStep(4); // Go to success screen
    } else {
      setCurrentStep(3); // Go to checkout
    }
  };
  const handleCheckout = () => {
    setIsProcessing(true);
    // Simulate payment processing
    setTimeout(() => {
      setIsProcessing(false);
      setCurrentStep(4); // Go to success screen
    }, 1500);
  };
  const handleComplete = () => {
    onSelectPlan(selectedPlan || 'basic');
    onClose();
  };
  const renderTooltip = (text: string) => {
    return <div className="group relative inline-block" data-id="element-13861">
        <HelpCircle size={14} className="ml-1 text-gray-400 cursor-help" data-id="element-13862" />
        <div className="absolute left-0 bottom-full mb-2 w-64 p-2 bg-white dark:bg-gray-800 text-xs text-gray-600 dark:text-gray-300 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-50 pointer-events-none" data-id="element-13863">
          {text}
        </div>
      </div>;
  };
  const getPlanDetails = (plan: string) => {
    switch (plan) {
      case 'basic':
        return {
          name: 'Basic AI Plan',
          price: 'Free',
          commission: '15%',
          color: 'blue',
          features: ['Basic AI-powered Angel', 'Standard customer routing', 'Limited monetization tools', 'Basic analytics'],
          limitations: ['No AI Studio access', 'No social feed posting', 'No exclusive upsells']
        };
      case 'pro':
        return {
          name: 'Hybrid Pro Plan',
          price: '$29.99/month',
          commission: '10%',
          color: 'purple',
          features: ['Advanced AI + Human routing', 'Social Starter Plan included', 'Full monetization toolkit', 'Advanced analytics dashboard', 'Limited AI Studio access (10 credits/mo)', 'Basic social feed posting'],
          limitations: ['Limited exclusive upsells']
        };
      case 'specialist':
        return {
          name: 'Social Specialist Plan',
          price: '$99.99/month',
          commission: '8.5%',
          color: 'green',
          features: ['Premium AI + Human routing', 'Unlimited AI Studio access', 'Full SinShift feed posting plan', 'Exclusive upsells (private content, skins)', 'Enterprise-grade analytics', 'Priority customer support', 'Custom branding options'],
          limitations: []
        };
      default:
        return {
          name: '',
          price: '',
          commission: '',
          color: '',
          features: [],
          limitations: []
        };
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-13864">
      <div className="w-full max-w-6xl max-h-[90vh] overflow-auto bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-6 animate-in zoom-in-95 duration-300" data-id="element-13865">
        {/* Header with step indicator */}
        <div className="flex justify-between items-center mb-6" data-id="element-13866">
          <div data-id="element-13867">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-13868">
              {currentStep === 1 ? 'Choose Your Plan — Boost Your Angel Performance' : currentStep === 2 ? 'Confirm Your Selection' : currentStep === 3 ? 'Complete Your Purchase' : 'Success! Your Plan Is Active'}
            </h2>
            {currentStep < 4 && <div className="flex items-center mt-2 space-x-1" data-id="element-13869">
                <div className={`h-2 w-12 rounded-full ${currentStep >= 1 ? 'bg-purple-500' : 'bg-gray-200 dark:bg-gray-700'}`} data-id="element-13870"></div>
                <div className={`h-2 w-12 rounded-full ${currentStep >= 2 ? 'bg-purple-500' : 'bg-gray-200 dark:bg-gray-700'}`} data-id="element-13871"></div>
                <div className={`h-2 w-12 rounded-full ${currentStep >= 3 ? 'bg-purple-500' : 'bg-gray-200 dark:bg-gray-700'}`} data-id="element-13872"></div>
                <div className={`h-2 w-12 rounded-full ${currentStep >= 4 ? 'bg-purple-500' : 'bg-gray-200 dark:bg-gray-700'}`} data-id="element-13873"></div>
                <span className="ml-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-13874">
                  Step {currentStep} of 4
                </span>
              </div>}
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" data-id="element-13875">
            <X size={20} className="text-gray-500 dark:text-gray-400" data-id="element-13876" />
          </button>
        </div>
        {/* Step 1: Plan Selection */}
        {currentStep === 1 && <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-13877">
              {/* Basic AI Plan */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-id="element-13878">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700" data-id="element-13879">
                  <div className="flex justify-between items-start" data-id="element-13880">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-13881">
                      Basic AI Plan
                    </h3>
                  </div>
                  <div className="flex items-baseline" data-id="element-13882">
                    <span className="text-3xl font-bold text-gray-900 dark:text-white" data-id="element-13883">
                      Free
                    </span>
                  </div>
                  <div className="mt-2 inline-block px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 rounded-full text-sm font-medium flex items-center" data-id="element-13884">
                    <Percent size={14} className="mr-1" data-id="element-13885" />
                    15% AI Commission
                    {renderTooltip('Fee applied to AI-generated revenue. Lower commission = more profit for you.')}
                  </div>
                </div>
                <div className="p-6" data-id="element-13886">
                  <ul className="space-y-3" data-id="element-13887">
                    <li className="flex items-start" data-id="element-13888">
                      <Check size={18} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-13889" />
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-13890">
                        Basic AI-powered Angel
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-13891">
                      <Check size={18} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-13892" />
                      <div className="flex items-center" data-id="element-13893">
                        <span className="text-gray-700 dark:text-gray-300" data-id="element-13894">
                          Standard customer routing
                        </span>
                        {renderTooltip('Basic algorithm routes customers to AI or human based on simple rules.')}
                      </div>
                    </li>
                    <li className="flex items-start" data-id="element-13895">
                      <Check size={18} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-13896" />
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-13897">
                        Limited monetization tools
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-13898">
                      <Check size={18} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-13899" />
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-13900">
                        Basic analytics
                      </span>
                    </li>
                    <li className="flex items-start opacity-50" data-id="element-13901">
                      <X size={18} className="text-red-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-13902" />
                      <div className="flex items-center" data-id="element-13903">
                        <span className="text-gray-700 dark:text-gray-300" data-id="element-13904">
                          No AI Studio access
                        </span>
                        {renderTooltip('AI Studio lets you create custom images and content for your Angel.')}
                      </div>
                    </li>
                    <li className="flex items-start opacity-50" data-id="element-13905">
                      <X size={18} className="text-red-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-13906" />
                      <div className="flex items-center" data-id="element-13907">
                        <span className="text-gray-700 dark:text-gray-300" data-id="element-13908">
                          No social feed posting
                        </span>
                        {renderTooltip('Social feed posts increase visibility and engagement with your Angel.')}
                      </div>
                    </li>
                    <li className="flex items-start opacity-50" data-id="element-13909">
                      <X size={18} className="text-red-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-13910" />
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-13911">
                        No exclusive upsells
                      </span>
                    </li>
                  </ul>
                  <button onClick={() => handleSelectPlan('basic')} className="w-full mt-6 py-2 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg font-medium hover:from-gray-600 hover:to-gray-700 transition-colors" data-id="element-13912">
                    Select Plan
                  </button>
                </div>
              </div>
              {/* Hybrid Pro Plan */}
              <div className="bg-white dark:bg-gray-800 border-2 border-blue-500 dark:border-blue-400 rounded-xl overflow-hidden shadow-md transform scale-105 relative" data-id="element-13913">
                <div className="absolute top-0 right-0 bg-blue-500 text-white px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-bl-lg" data-id="element-13914">
                  RECOMMENDED
                </div>
                <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20" data-id="element-13915">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-13916">
                    Hybrid Pro Plan
                  </h3>
                  <div className="flex items-baseline" data-id="element-13917">
                    <span className="text-3xl font-bold text-gray-900 dark:text-white" data-id="element-13918">
                      $29.99
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 ml-1" data-id="element-13919">
                      /month
                    </span>
                  </div>
                  <div className="mt-2 inline-block px-3 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 rounded-full text-sm font-medium flex items-center" data-id="element-13920">
                    <Percent size={14} className="mr-1" data-id="element-13921" />
                    10% AI Commission
                    {renderTooltip('Fee applied to AI-generated revenue. Lower commission = more profit for you.')}
                  </div>
                </div>
                <div className="p-6" data-id="element-13922">
                  <ul className="space-y-3" data-id="element-13923">
                    <li className="flex items-start" data-id="element-13924">
                      <Check size={18} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-13925" />
                      <div className="flex items-center" data-id="element-13926">
                        <span className="text-gray-700 dark:text-gray-300" data-id="element-13927">
                          Advanced AI + Human routing
                        </span>
                        {renderTooltip('Smart routing prioritizes high-value customers for human interaction and automates low-value ones.')}
                      </div>
                    </li>
                    <li className="flex items-start" data-id="element-13928">
                      <Check size={18} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-13929" />
                      <div className="flex items-center" data-id="element-13930">
                        <span className="text-gray-700 dark:text-gray-300" data-id="element-13931">
                          Social Starter Plan included
                        </span>
                        {renderTooltip('Basic social media presence with scheduled posts and engagement tools.')}
                      </div>
                    </li>
                    <li className="flex items-start" data-id="element-13932">
                      <Check size={18} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-13933" />
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-13934">
                        Full monetization toolkit
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-13935">
                      <Check size={18} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-13936" />
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-13937">
                        Advanced analytics dashboard
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-13938">
                      <Check size={18} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-13939" />
                      <div className="flex items-center" data-id="element-13940">
                        <span className="text-gray-700 dark:text-gray-300" data-id="element-13941">
                          Limited AI Studio access (10 credits/mo)
                        </span>
                        {renderTooltip('Create custom images and content for your Angel with 10 monthly AI credits.')}
                      </div>
                    </li>
                    <li className="flex items-start" data-id="element-13942">
                      <Check size={18} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-13943" />
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-13944">
                        Basic social feed posting
                      </span>
                    </li>
                    <li className="flex items-start opacity-50" data-id="element-13945">
                      <X size={18} className="text-red-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-13946" />
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-13947">
                        Limited exclusive upsells
                      </span>
                    </li>
                  </ul>
                  <button onClick={() => handleSelectPlan('pro')} className="w-full mt-6 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg font-medium hover:from-blue-600 hover:to-indigo-600 transition-colors" data-id="element-13948">
                    Select Plan
                  </button>
                </div>
              </div>
              {/* Social Specialist Plan */}
              <div className="bg-white dark:bg-gray-800 border border-purple-200 dark:border-purple-700 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-id="element-13949">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20" data-id="element-13950">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-13951">
                    Social Specialist Plan
                  </h3>
                  <div className="flex items-baseline" data-id="element-13952">
                    <span className="text-3xl font-bold text-gray-900 dark:text-white" data-id="element-13953">
                      $99.99
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 ml-1" data-id="element-13954">
                      /month
                    </span>
                  </div>
                  <div className="mt-2 inline-block px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-sm font-medium flex items-center" data-id="element-13955">
                    <Percent size={14} className="mr-1" data-id="element-13956" />
                    8.5% AI Commission
                    {renderTooltip('Lowest fee applied to AI-generated revenue. Maximum profit for you.')}
                  </div>
                </div>
                <div className="p-6" data-id="element-13957">
                  <ul className="space-y-3" data-id="element-13958">
                    <li className="flex items-start" data-id="element-13959">
                      <Check size={18} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-13960" />
                      <div className="flex items-center" data-id="element-13961">
                        <span className="text-gray-700 dark:text-gray-300" data-id="element-13962">
                          Premium AI + Human routing
                        </span>
                        {renderTooltip('Advanced ML-based routing optimizes customer value and engagement.')}
                      </div>
                    </li>
                    <li className="flex items-start" data-id="element-13963">
                      <Check size={18} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-13964" />
                      <div className="flex items-center" data-id="element-13965">
                        <span className="text-gray-700 dark:text-gray-300" data-id="element-13966">
                          Unlimited AI Studio access
                        </span>
                        {renderTooltip('Create unlimited custom images and content for your Angel.')}
                      </div>
                    </li>
                    <li className="flex items-start" data-id="element-13967">
                      <Check size={18} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-13968" />
                      <div className="flex items-center" data-id="element-13969">
                        <span className="text-gray-700 dark:text-gray-300" data-id="element-13970">
                          Full SinShift feed posting plan
                        </span>
                        {renderTooltip('Comprehensive social strategy with optimal posting schedule, engagement boosters, and premium placement.')}
                      </div>
                    </li>
                    <li className="flex items-start" data-id="element-13971">
                      <Check size={18} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-13972" />
                      <div className="flex items-center font-medium text-purple-600 dark:text-purple-400" data-id="element-13973">
                        <Lock size={14} className="mr-1" data-id="element-13974" />
                        <span data-id="element-13975">
                          Exclusive upsells (private content, skins, avatars)
                        </span>
                        {renderTooltip('Offer premium content and features that other Angels cannot access.')}
                      </div>
                    </li>
                    <li className="flex items-start" data-id="element-13976">
                      <Check size={18} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-13977" />
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-13978">
                        Enterprise-grade analytics
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-13979">
                      <Check size={18} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-13980" />
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-13981">
                        Priority customer support
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-13982">
                      <Check size={18} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-13983" />
                      <div className="flex items-center font-medium text-purple-600 dark:text-purple-400" data-id="element-13984">
                        <Crown size={14} className="mr-1" data-id="element-13985" />
                        <span data-id="element-13986">Custom branding options</span>
                        {renderTooltip('Personalize your Angel with custom branding elements and unique visual identity.')}
                      </div>
                    </li>
                  </ul>
                  <button onClick={() => handleSelectPlan('specialist')} className="w-full mt-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-medium hover:from-purple-600 hover:to-pink-600 transition-colors" data-id="element-13987">
                    Select Plan
                  </button>
                </div>
              </div>
            </div>
            <div className="flex justify-end" data-id="element-13988">
              <button onClick={onClose} className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" data-id="element-13989">
                Maybe Later
              </button>
            </div>
          </>}
        {/* Step 2: Confirmation Screen */}
        {currentStep === 2 && selectedPlan && <>
            <div className="mb-8" data-id="element-13990">
              {/* Selected plan card */}
              <div className={`max-w-2xl mx-auto bg-white dark:bg-gray-800 border-2 ${selectedPlan === 'basic' ? 'border-gray-300 dark:border-gray-600' : selectedPlan === 'pro' ? 'border-blue-500 dark:border-blue-400' : 'border-purple-500 dark:border-purple-400'} rounded-xl overflow-hidden shadow-md mb-8`} data-id="element-13991">
                <div className={`p-6 border-b border-gray-200 dark:border-gray-700 ${selectedPlan === 'basic' ? 'bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700' : selectedPlan === 'pro' ? 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20' : 'bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20'}`} data-id="element-13992">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-13993">
                    {getPlanDetails(selectedPlan).name}
                  </h3>
                  <div className="flex items-baseline" data-id="element-13994">
                    <span className="text-3xl font-bold text-gray-900 dark:text-white" data-id="element-13995">
                      {getPlanDetails(selectedPlan).price}
                    </span>
                  </div>
                  <div className={`mt-2 inline-block px-3 py-1 ${selectedPlan === 'basic' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' : selectedPlan === 'pro' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300' : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'} rounded-full text-sm font-medium flex items-center`} data-id="element-13996">
                    <Percent size={14} className="mr-1" data-id="element-13997" />
                    {getPlanDetails(selectedPlan).commission} AI Commission
                  </div>
                </div>
                <div className="p-6" data-id="element-13998">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3" data-id="element-13999">
                    Plan Features:
                  </h4>
                  <ul className="space-y-2 mb-4" data-id="element-14000">
                    {getPlanDetails(selectedPlan).features.map((feature, index) => <li key={index} className="flex items-start" data-id="element-14001">
                          <Check size={16} className="text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-14002" />
                          <span className="text-gray-700 dark:text-gray-300" data-id="element-14003">
                            {feature}
                          </span>
                        </li>)}
                  </ul>
                </div>
              </div>
              {/* Summary table */}
              <div className="max-w-2xl mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-8" data-id="element-14004">
                <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700" data-id="element-14005">
                  <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-14006">
                    Plan Summary
                  </h3>
                </div>
                <div className="p-6" data-id="element-14007">
                  <div className="space-y-4" data-id="element-14008">
                    <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700" data-id="element-14009">
                      <span className="text-gray-600 dark:text-gray-400" data-id="element-14010">
                        AI Commission Rate
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white" data-id="element-14011">
                        {getPlanDetails(selectedPlan).commission}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700" data-id="element-14012">
                      <span className="text-gray-600 dark:text-gray-400" data-id="element-14013">
                        Human Operator Commission
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white" data-id="element-14014">
                        30%
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700" data-id="element-14015">
                      <span className="text-gray-600 dark:text-gray-400" data-id="element-14016">
                        Monthly Fee
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white" data-id="element-14017">
                        {selectedPlan === 'basic' ? 'Free' : selectedPlan === 'pro' ? '$29.99' : '$99.99'}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700" data-id="element-14018">
                      <span className="text-gray-600 dark:text-gray-400" data-id="element-14019">
                        AI Studio Credits
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white" data-id="element-14020">
                        {selectedPlan === 'basic' ? 'None' : selectedPlan === 'pro' ? '10 per month' : 'Unlimited'}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700" data-id="element-14021">
                      <span className="text-gray-600 dark:text-gray-400" data-id="element-14022">
                        Social Feed Plan
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white" data-id="element-14023">
                        {selectedPlan === 'basic' ? 'None' : selectedPlan === 'pro' ? 'Basic' : 'Premium'}
                      </span>
                    </div>
                    <div className="flex justify-between py-2" data-id="element-14024">
                      <span className="text-gray-600 dark:text-gray-400" data-id="element-14025">
                        Exclusive Upsells
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white" data-id="element-14026">
                        {selectedPlan === 'basic' ? 'None' : selectedPlan === 'pro' ? 'Limited' : 'Full Access'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              {/* Optional add-ons */}
              <div className="max-w-2xl mx-auto bg-gray-50 dark:bg-gray-800/50 rounded-xl p-6 mb-8" data-id="element-14027">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4" data-id="element-14028">
                  Optional Add-ons
                </h3>
                <div className="space-y-4" data-id="element-14029">
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4" data-id="element-14030">
                    <div className="flex items-center justify-between mb-3" data-id="element-14031">
                      <div className="flex items-center" data-id="element-14032">
                        <Clock size={18} className="text-blue-500 mr-2" data-id="element-14033" />
                        <h4 className="font-medium text-gray-900 dark:text-white" data-id="element-14034">
                          Human Operator Hours
                        </h4>
                      </div>
                      <input type="checkbox" checked={selectedAddons.includes('operator')} onChange={() => toggleAddon('operator')} className="h-5 w-5 text-purple-600 dark:text-purple-400 rounded border-gray-300 dark:border-gray-600 focus:ring-purple-500 dark:focus:ring-purple-400" data-id="element-14035" />
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2" data-id="element-14036">
                      Outsourced operators to handle your VIP customers
                    </p>
                    <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-14037">
                      $19.99/hour
                    </div>
                  </div>
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4" data-id="element-14038">
                    <div className="flex items-center justify-between mb-3" data-id="element-14039">
                      <div className="flex items-center" data-id="element-14040">
                        <Camera size={18} className="text-purple-500 mr-2" data-id="element-14041" />
                        <h4 className="font-medium text-gray-900 dark:text-white" data-id="element-14042">
                          Extra AI Studio Credits
                        </h4>
                      </div>
                      <input type="checkbox" checked={selectedAddons.includes('ai_credits')} onChange={() => toggleAddon('ai_credits')} className="h-5 w-5 text-purple-600 dark:text-purple-400 rounded border-gray-300 dark:border-gray-600 focus:ring-purple-500 dark:focus:ring-purple-400" data-id="element-14043" />
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2" data-id="element-14044">
                      Additional AI image and content generation credits
                    </p>
                    <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-14045">
                      $29.99/50 credits
                    </div>
                  </div>
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4" data-id="element-14046">
                    <div className="flex items-center justify-between mb-3" data-id="element-14047">
                      <div className="flex items-center" data-id="element-14048">
                        <Globe size={18} className="text-green-500 mr-2" data-id="element-14049" />
                        <h4 className="font-medium text-gray-900 dark:text-white" data-id="element-14050">
                          External Social Posting
                        </h4>
                      </div>
                      <input type="checkbox" checked={selectedAddons.includes('external')} onChange={() => toggleAddon('external')} className="h-5 w-5 text-purple-600 dark:text-purple-400 rounded border-gray-300 dark:border-gray-600 focus:ring-purple-500 dark:focus:ring-purple-400" data-id="element-14051" />
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2" data-id="element-14052">
                      Automatically post to external social media platforms
                    </p>
                    <div className="flex space-x-2 mb-2" data-id="element-14053">
                      <Instagram size={16} className="text-pink-500" data-id="element-14054" />
                      <Twitter size={16} className="text-blue-400" data-id="element-14055" />
                    </div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-14056">
                      $49.99/month
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-between" data-id="element-14057">
              <button onClick={() => setCurrentStep(1)} className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center" data-id="element-14058">
                <ChevronLeft size={16} className="mr-1" data-id="element-14059" />
                Back
              </button>
              <button onClick={handleConfirm} className={`px-6 py-2 ${selectedPlan === 'basic' ? 'bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700' : selectedPlan === 'pro' ? 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600' : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600'} text-white rounded-lg font-medium transition-colors flex items-center`} data-id="element-14060">
                <span data-id="element-14061">
                  {selectedPlan === 'basic' ? 'Activate Free Plan' : 'Continue to Checkout'}
                </span>
                <ChevronRight size={16} className="ml-1" data-id="element-14062" />
              </button>
            </div>
          </>}
        {/* Step 3: Checkout (Paid Plans Only) */}
        {currentStep === 3 && selectedPlan && selectedPlan !== 'basic' && <>
            <div className="max-w-2xl mx-auto mb-8" data-id="element-14063">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-8" data-id="element-14064">
                <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700" data-id="element-14065">
                  <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-14066">
                    Payment Details
                  </h3>
                </div>
                <div className="p-6" data-id="element-14067">
                  <div className="mb-6" data-id="element-14068">
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-14069">
                      {getPlanDetails(selectedPlan).name}
                    </h4>
                    <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700" data-id="element-14070">
                      <span className="text-gray-600 dark:text-gray-400" data-id="element-14071">
                        Monthly Fee
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white" data-id="element-14072">
                        {getPlanDetails(selectedPlan).price}
                      </span>
                    </div>
                    {selectedAddons.length > 0 && <>
                        {selectedAddons.includes('operator') && <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700" data-id="element-14073">
                            <span className="text-gray-600 dark:text-gray-400" data-id="element-14074">
                              Human Operator Hours
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white" data-id="element-14075">
                              $19.99
                            </span>
                          </div>}
                        {selectedAddons.includes('ai_credits') && <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700" data-id="element-14076">
                            <span className="text-gray-600 dark:text-gray-400" data-id="element-14077">
                              Extra AI Studio Credits
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white" data-id="element-14078">
                              $29.99
                            </span>
                          </div>}
                        {selectedAddons.includes('external') && <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700" data-id="element-14079">
                            <span className="text-gray-600 dark:text-gray-400" data-id="element-14080">
                              External Social Posting
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white" data-id="element-14081">
                              $49.99
                            </span>
                          </div>}
                      </>}
                    <div className="flex justify-between py-2 mt-2 border-t-2 border-gray-200 dark:border-gray-700" data-id="element-14082">
                      <span className="font-medium text-gray-900 dark:text-white" data-id="element-14083">
                        Total
                      </span>
                      <span className="font-bold text-gray-900 dark:text-white" data-id="element-14084">
                        $
                        {((selectedPlan === 'pro' ? 29.99 : 99.99) + (selectedAddons.includes('operator') ? 19.99 : 0) + (selectedAddons.includes('ai_credits') ? 29.99 : 0) + (selectedAddons.includes('external') ? 49.99 : 0)).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  {/* Payment form */}
                  <div className="space-y-4" data-id="element-14085">
                    <div data-id="element-14086">
                      <label htmlFor="card-number" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-14087">
                        Card Number
                      </label>
                      <div className="relative" data-id="element-14088">
                        <input type="text" id="card-number" placeholder="1234 5678 9012 3456" className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-14089" />
                        <CreditCard size={18} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-14090" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4" data-id="element-14091">
                      <div data-id="element-14092">
                        <label htmlFor="expiry" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-14093">
                          Expiry Date
                        </label>
                        <input type="text" id="expiry" placeholder="MM/YY" className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-14094" />
                      </div>
                      <div data-id="element-14095">
                        <label htmlFor="cvc" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-14096">
                          CVC
                        </label>
                        <input type="text" id="cvc" placeholder="123" className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-14097" />
                      </div>
                    </div>
                    <div data-id="element-14098">
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-14099">
                        Name on Card
                      </label>
                      <input type="text" id="name" placeholder="John Doe" className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-14100" />
                    </div>
                  </div>
                  <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 text-sm text-blue-700 dark:text-blue-300 flex items-start" data-id="element-14101">
                    <Info size={16} className="mr-2 mt-0.5 flex-shrink-0" data-id="element-14102" />
                    <div data-id="element-14103">
                      <p className="font-medium" data-id="element-14104">Subscription Details</p>
                      <p className="mt-1" data-id="element-14105">
                        You'll be charged {getPlanDetails(selectedPlan).price}{' '}
                        today. Your subscription will renew automatically each
                        month until canceled. You can cancel anytime from your
                        account settings.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-between" data-id="element-14106">
              <button onClick={() => setCurrentStep(2)} className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center" data-id="element-14107">
                <ChevronLeft size={16} className="mr-1" data-id="element-14108" />
                Back
              </button>
              <button onClick={handleCheckout} disabled={isProcessing} className={`px-6 py-2 ${selectedPlan === 'pro' ? 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600' : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600'} text-white rounded-lg font-medium transition-colors flex items-center ${isProcessing ? 'opacity-75 cursor-not-allowed' : ''}`} data-id="element-14109">
                {isProcessing ? <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-14110">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-14111"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-14112"></path>
                    </svg>
                    Processing...
                  </> : <>
                    <span data-id="element-14113">Activate Plan</span>
                    <ChevronRight size={16} className="ml-1" data-id="element-14114" />
                  </>}
              </button>
            </div>
          </>}
        {/* Step 4: Success Screen */}
        {currentStep === 4 && selectedPlan && <div className="max-w-xl mx-auto text-center" data-id="element-14115">
            <div className="mb-6 flex justify-center" data-id="element-14116">
              <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center" data-id="element-14117">
                <CheckCircle size={40} className="text-green-500 dark:text-green-400" data-id="element-14118" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-14119">
              ✅ Your {getPlanDetails(selectedPlan).name} is now active!
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-8" data-id="element-14120">
              You now pay {getPlanDetails(selectedPlan).commission} AI
              commission and 30% Human commission. Access your perks in the
              Monetization tab.
            </p>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 mb-8" data-id="element-14121">
              <div className="flex items-center justify-center mb-4" data-id="element-14122">
                <Award size={24} className={`${selectedPlan === 'basic' ? 'text-gray-500' : selectedPlan === 'pro' ? 'text-blue-500' : 'text-purple-500'} mr-2`} data-id="element-14123" />
                <h4 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-14124">
                  Plan Benefits Activated
                </h4>
              </div>
              <ul className="space-y-2 text-left" data-id="element-14125">
                {selectedPlan === 'basic' ? <>
                    <li className="flex items-start" data-id="element-14126">
                      <Check size={16} className="text-green-500 mt-1 mr-2 flex-shrink-0" data-id="element-14127" />
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-14128">
                        Basic AI-powered Angel ready to go
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-14129">
                      <Check size={16} className="text-green-500 mt-1 mr-2 flex-shrink-0" data-id="element-14130" />
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-14131">
                        15% AI Commission Rate applied
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-14132">
                      <Check size={16} className="text-green-500 mt-1 mr-2 flex-shrink-0" data-id="element-14133" />
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-14134">
                        Basic monetization tools unlocked
                      </span>
                    </li>
                  </> : selectedPlan === 'pro' ? <>
                    <li className="flex items-start" data-id="element-14135">
                      <Check size={16} className="text-green-500 mt-1 mr-2 flex-shrink-0" data-id="element-14136" />
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-14137">
                        10% AI Commission Rate applied (33% savings!)
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-14138">
                      <Check size={16} className="text-green-500 mt-1 mr-2 flex-shrink-0" data-id="element-14139" />
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-14140">
                        10 AI Studio credits added to your account
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-14141">
                      <Check size={16} className="text-green-500 mt-1 mr-2 flex-shrink-0" data-id="element-14142" />
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-14143">
                        Advanced AI + Human routing activated
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-14144">
                      <Check size={16} className="text-green-500 mt-1 mr-2 flex-shrink-0" data-id="element-14145" />
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-14146">
                        Social Starter Plan now available
                      </span>
                    </li>
                  </> : <>
                    <li className="flex items-start" data-id="element-14147">
                      <Check size={16} className="text-green-500 mt-1 mr-2 flex-shrink-0" data-id="element-14148" />
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-14149">
                        8.5% AI Commission Rate applied (43% savings!)
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-14150">
                      <Check size={16} className="text-green-500 mt-1 mr-2 flex-shrink-0" data-id="element-14151" />
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-14152">
                        Unlimited AI Studio access unlocked
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-14153">
                      <Check size={16} className="text-green-500 mt-1 mr-2 flex-shrink-0" data-id="element-14154" />
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-14155">
                        Premium AI + Human routing activated
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-14156">
                      <Check size={16} className="text-green-500 mt-1 mr-2 flex-shrink-0" data-id="element-14157" />
                      <span className="text-gray-700 dark:text-gray-300 font-medium text-purple-600 dark:text-purple-400 flex items-center" data-id="element-14158">
                        <Lock size={14} className="mr-1" data-id="element-14159" />
                        Exclusive upsells now available
                      </span>
                    </li>
                  </>}
                {selectedAddons.length > 0 && selectedAddons.map(addon => <li key={addon} className="flex items-start" data-id="element-14160">
                      <Check size={16} className="text-green-500 mt-1 mr-2 flex-shrink-0" data-id="element-14161" />
                      <span className="text-gray-700 dark:text-gray-300" data-id="element-14162">
                        {addon === 'operator' ? 'Human Operator Hours added' : addon === 'ai_credits' ? 'Extra AI Studio Credits added' : 'External Social Posting activated'}
                      </span>
                    </li>)}
              </ul>
            </div>
            <button onClick={handleComplete} className={`px-6 py-3 ${selectedPlan === 'basic' ? 'bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700' : selectedPlan === 'pro' ? 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600' : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600'} text-white rounded-lg font-medium transition-colors flex items-center mx-auto`} data-id="element-14163">
              <Settings size={16} className="mr-2" data-id="element-14164" />
              <span data-id="element-14165">Go to Monetization Settings</span>
              <ArrowRight size={16} className="ml-2" data-id="element-14166" />
            </button>
          </div>}
      </div>
    </div>;
};